"use client"

import ImageModal from "@/(admin)/components/image/select-image-button"
import { t } from "@/languages/i18n"
import { MediaData } from "@/lib/config/types"
import { DeleteOutlined } from "@ant-design/icons"
import { App, Button, Col, DatePicker, Form, Input, Row } from "antd"
import { FormInstance } from "antd/lib"
import React, { useState } from "react"

interface LetakyFormFieldsProps {
  form: FormInstance
  catalogCount?: number
  featuredImageValue?: MediaData[]
  setFeaturedImageValue: (val: MediaData[]) => void
  catalogImagesValue?: MediaData[]
  setCatalogImagesValue: (val: MediaData[]) => void
  pdfFileValue?: MediaData[]
  setPdfFileValue: (val: MediaData[]) => void
}

const LetakyFormFields: React.FC<LetakyFormFieldsProps> = ({
  form,
  catalogCount = 0,
  featuredImageValue,
  setFeaturedImageValue,
  catalogImagesValue,
  setCatalogImagesValue,
  pdfFileValue,
  setPdfFileValue,
}) => {
  const { TextArea } = Input
  const [catalogListOpen, setCatalogListOpen] = useState(false)
  const { modal } = App.useApp()

  const handleDeleteAllCatalog = () => {
    modal.confirm({
      title: t("Xác nhận xóa tất cả"),
      content: t("Bạn có muốn xóa tất cả ảnh catalog không?"),
      okText: t("Xóa"),
      cancelText: t("Hủy"),
      okButtonProps: { danger: true },
      onOk: () => {
        setCatalogImagesValue([])
        form.setFieldValue("catalogImages", [])
      },
    })
  }

  return (
    <>
      <Form.Item
        className="w-full"
        name="title"
        label={<span className="text-base font-medium">{t("Title")}</span>}
        rules={[{ required: true, message: t("Please input title!") }]}
      >
        <Input placeholder={t("Input title...")} />
      </Form.Item>

      <Form.Item
        className="w-full"
        name="description"
        label={<span className="text-base font-medium">{t("Description")}</span>}
      >
        <TextArea placeholder={t("Input description...")} rows={4} />
      </Form.Item>

      <Row gutter={24} className="w-full">
        <Col span={12} xs={24} sm={12}>
          <Form.Item
            label={<span className="text-base font-medium">{t("Thời điểm bắt đầu")}</span>}
            name="dateStart"
            rules={[{ required: true, message: t("Please select start date!") }]}
          >
            <DatePicker showTime needConfirm={false} placeholder={t("Chọn thời điểm")} format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12} xs={24} sm={12}>
          <Form.Item
            label={<span className="text-base font-medium">{t("Thời điểm kết thúc")}</span>}
            name="dateEnd"
            rules={[
              { required: true, message: t("Please select end date!") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue("dateStart")
                  if (!value || !startDate) {
                    return Promise.resolve()
                  }
                  if (value.isAfter(startDate)) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error(t("Thời điểm kết thúc phải sau thời điểm bắt đầu")))
                },
              }),
            ]}
          >
            <DatePicker
              showTime
              needConfirm={false}
              format="DD/MM/YYYY HH:mm"
              placeholder={t("Chọn thời điểm")}
              style={{ width: "100%" }}
              disabledDate={current => {
                const startDate = form.getFieldValue("dateStart")
                return current && startDate && current.isBefore(startDate, "day")
              }}
            />
          </Form.Item>
        </Col>
      </Row>


      <Form.Item
        label={<span className="text-base font-medium">{t("Featured Image")}</span>}
        name="featuredImage"
        rules={[{ required: true, message: t("Please select featured image!") }]}
      >
        <ImageModal
          selectedMedia={featuredImageValue}
          setSelectedMedia={media => {
            setFeaturedImageValue(media)
            form.setFieldValue("featuredImage", media.length > 0 ? media[0].id : null)
          }}
          selectMode="single"
        />
      </Form.Item>

      <Form.Item
        label={
          <div className="flex items-center justify-between w-full gap-2">
            <span className="text-base font-medium">
              {t("Ảnh Catalog:")} {catalogCount > 0 && `${catalogCount} ảnh`}
            </span>
            {catalogCount > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteAllCatalog}
                >
                  {t("Xóa tất cả")}
                </Button>
              </div>
            )}
          </div>
        }
        name="catalogImages"
      >
        <ImageModal
          selectedMedia={catalogImagesValue}
          setSelectedMedia={media => {
            setCatalogImagesValue(media)
            form.setFieldValue(
              "catalogImages",
              media.map(m => m.id),
            )
          }}
          selectMode="multi"
        />
      </Form.Item>

      <Form.Item label={<span className="text-base font-medium">{t("PDF File")}</span>} name="pdfFile">
        <ImageModal
          selectedMedia={pdfFileValue}
          setSelectedMedia={media => {
            setPdfFileValue(media)
            form.setFieldValue("pdfFile", media.length > 0 ? media[0].id : null)
          }}
          selectMode="single"
          type="pdf"
        />
      </Form.Item>
    </>
  )
}

export default LetakyFormFields
